import { useEffect, useRef } from "react";

/**
 * VoicePlayer — drop-in replacement for `<audio controls src=…>` that fixes
 * the well-known WebM duration bug in old MediaRecorder uploads.
 *
 * On `loadedmetadata`, if the browser reports `duration === Infinity`
 * (which happens for blobs that lacked WebM duration headers), this seeks
 * to a huge time to force the browser to actually compute the duration,
 * then resets currentTime to 0.
 *
 * For NEW recordings the VoiceRecorder now uses `fix-webm-duration` so the
 * blob already has correct metadata — but this fallback is still useful
 * for messages uploaded before the fix.
 */
export default function VoicePlayer({ src, className, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let fixed = false;
    const onLoadedMetadata = () => {
      if (el.duration === Infinity || isNaN(el.duration)) {
        // Force the browser to compute duration
        const onTimeUpdate = () => {
          if (!fixed) {
            fixed = true;
            try { el.currentTime = 0; } catch { /* ignore */ }
            el.removeEventListener("timeupdate", onTimeUpdate);
          }
        };
        el.addEventListener("timeupdate", onTimeUpdate);
        try { el.currentTime = 1e101; } catch { /* ignore */ }
      }
    };
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => { el.removeEventListener("loadedmetadata", onLoadedMetadata); };
  }, [src]);

  return <audio ref={ref} controls src={src} className={className} {...rest} />;
}
